<template>
  <div class="position-relative">
    <router-link :to="{ name: 'books.show', params: { username: book ? book.user.username : 'username', slug: book && book.slug ? book.slug : 'slug' } }">
      <div class="book-card card-small">
        <div class="image-wrap" :class="imageClasses">
          <div class="image" :style="coverImage"></div>
          <img class="cover-sizing" src="/images/default/card-no-cover.png" alt="" />
        </div>
        <Button
          class="btn-circle btn-select shadow-default"
          :class="{
            'selected': selected
          }"
          v-if="isSelectAvailable"
          @click.prevent="selectBook"
        >
          <span></span>
        </Button>
        <div class="header-info">
          <div class="w-100 d-flex align-items-center">
            <BaseIcon
              class="icon mr-2"
              size="xSmall"
              v-if="book && user && book.visibility === 'private' && book.user.id === user.id"
            >
              <LockIcon />
            </BaseIcon>
            <div class="author-image" :style="avatarImage"></div>
            <span class="author-name">{{ book && book.user.username }}</span>
            <BaseIcon
              v-if="book && book.user.is_verified"
              size="xxSmall"
              class="ml-1"
            >
              <VerifiedIcon
                class="profile"
              />
            </BaseIcon>
          </div>
          <div class="d-flex justify-content-end align-items-center">
            <span
              class="view-count px-2 py-1 background-white rounded-xl shadow-light mr-2"
              v-if="book && !isSelectAvailable && book.status === 'published' && book.views_count >= 0"
            >
              <BaseIcon
                class="mr-1"
                size="xSmall"
              >
                <EyeIcon />
              </BaseIcon>
              <span class="text-color1">{{ book.views_count | formatAbbreviatedNumber }}</span>
            </span>
            <span
              class="view-count px-2 py-1 background-white rounded-xl shadow-light"
              v-if="book && book.status === 'published'"
            >
              <span class="icon-basic text-color2">
                <i class="fa fa-book"></i>
              </span>
            </span>
            <span
              class="px-2 py-1 background-white rounded-xl shadow-light text-color2 font-size-xSmall"
              v-else-if="book && book.status !== 'published'"
            >
              Rascunho
            </span>
            <span
              class="mx-1 py-1 background-white rounded-xl shadow-light font-size-xSmall ellipsis-block"
              v-if="!isSelectAvailable"
            >
              <b-dropdown
                toggle-class="p-0 bordered-0 d-flex dropdown-toggle"
                variant="none"
                menu-class="font-size-xSmall p-3 text-center dropdown-default"
                no-caret
                dropleft
              >
                <template v-slot:button-content>
                  <i class="fa fa-ellipsis-v px-3 text-color2" aria-hidden="true"></i>
                </template>
                <b-dropdown-item
                  link-class="p-0"
                  v-if="book && user && book.user.id === user.id"
                  :to="{ name: 'books.edit', params: { slug: book.slug } }"
                >
                  Editar
                  <hr class="h-1 background-color2" />
                </b-dropdown-item>
                <b-dropdown-item
                  link-class="p-0"
                  v-if="book && user && book.user.id === user.id"
                  v-b-modal="destroyModalId"
                >
                  Excluir
                </b-dropdown-item>
                <hr class="h-1 background-color2" v-if="book && user && book.user.id === user.id && book.status === 'published'"/>
                <b-dropdown-item
                  link-class="p-0"
                  v-if="book.status === 'published'"
                  v-b-modal="shareModalId"
                >
                  Compartilhar
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </div>
        </div>
        <div class="book-info">
          <div class="progress-bar" v-if="book && bookProgress > 0">
            <div class="bar">
              <div class="fill" :style="{width: bookProgress + '%'}"></div>
            </div>
            <div class="progress-info">
              <span>{{bookProgress}}%</span>
            </div>
          </div>
          <div class="book-title">
            <span
              v-if="book && book.amount > 0"
              class="usd-icon py-1 background-white rounded-xl shadow-light mr-2"
              tabindex="0"
              v-b-tooltip.hover="{ customClass: 'font-size-xSmall' }"
              title="Livro pago"
            >
                <span class="icon-basic text-color2">
                  <i class="fa fa-usd"></i>
                </span>
            </span>
            <h2 :title="book && book.title">{{ book && book.title }}</h2>
          </div>
        </div>
      </div>
    </router-link>
    <ShareModal
      :type="book.type"
      :target="book"
      :modalId="shareModalId"
    />
    <DestroyBookModal
      :book="book"
      :type="book && book.type"
      @book-destroyed="$emit('book-destroyed', book.id)"
      :modalId="destroyModalId"
    />
  </div>
</template>

<script>

import variables from '@/mixins/variables'
import { createUniqueId } from '@/utils'
import { mapState } from 'vuex'

import Button from '@/components/common/Button/Button.vue'
import BaseIcon from '@/components/common/Icon/BaseIcon.vue'
import EyeIcon from '@/components/common/Icon/EyeIcon.vue'
import LockIcon from '@/components/common/Icon/LockIcon.vue'
import VerifiedIcon from '@/components/common/Icon/VerifiedIcon.vue'
import ShareModal from '@/components/share/ShareModal.vue'
import DestroyBookModal from '@/components/books/DestroyBookModal.vue'

export default {
  name: 'bookThumbCard',
  props: ['book', 'isSelectAvailable', 'isDisabled'],
  mixins: [variables],
  components: {
    Button,
    BaseIcon,
    EyeIcon,
    LockIcon,
    VerifiedIcon,
    ShareModal,
    DestroyBookModal
  },
  data () {
    return {
      selected: false
    }
  },
  computed: {
    imageClasses () {
      return {
        'has-cover': this.book ? this.book.has_cover : false
      }
    },
    coverImage () {
      return {
        backgroundImage: this.book ? `url('${this.book.cover}')` : `url('${this.noCoverUrl}')`
      }
    },
    avatarImage () {
      return {
        backgroundImage: this.book ? `url('${this.book.user.avatar}')` : `url('${this.noAvatarUrl}')`
      }
    },
    bookProgress () {
      return (this.book) ? this.book.progress : 0
    },
    ...mapState('users', {
      user: state => state.user
    }),
    shareModalId () {
      return 'modal-share-' + createUniqueId()
    },
    destroyModalId () {
      return 'modal-destroy-' + createUniqueId()
    }
  },
  methods: {
    selectBook () {
      this.selected = !this.selected
      this.$emit('book-selected', { card: this.book, action: this.selected })
    }
  }
}
</script>

<style lang="scss" scoped>

.book-card {
  -webkit-border-radius: $radius_regular;
  -moz-border-radius: $radius_regular;
  border-radius: $radius_regular;
  overflow: hidden;
  -webkit-box-shadow: 0 0 10px 0 rgba(0,0,0,0.3);
  box-shadow: 0 0 10px 0 rgba(0,0,0,0.3);
  background-color: #fff;
  position: relative;
  color: #fff;

  &:hover {
    .image-wrap {
      .image {
        transform: scale(1.1);
      }
    }
  }

  .image-wrap {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.25);
    }

    .image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      transition: all 0.6s ease-out;
    }

    .cover-sizing {
      opacity: 0;
    }

    &.has-cover {
      &::after {
        background: rgba(0,0,0,0.1);
      }
    }
  }

  .btn-select {
    position: absolute;
    top: 15px;
    right: 15px;
    padding: 0;
    border: none;
    background: #fff;
    z-index: 2;
    color: $color4;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;

    &.selected {
      span {
        position: relative;
        background: $color2;
        border-color: $color2;

        &::after {
          content: '\f00c';
          font-family: "FontAwesome";
          position: absolute;
          top: 0px;
          left: 3px;
          color: #fff;
          font-size: 14px;
        }
      }
    }

    span {
      display: block;
      width: 22px;
      height: 22px;
      border-radius: 100px;
      border: 1px solid #ddd;
    }
  }

  .header-info {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;

    .view-count {
      display: flex;
      font-size: $font_size_xSmall;
      white-space: nowrap;

      .icon {
        margin-top: -1px;
      }
    }

    .author-image {
      -webkit-border-radius: 100px;
      -moz-border-radius: 100px;
      border-radius: 100px;
      background-size: cover;
      background-position: center;
      width: 30px;
      height: 30px;
      margin-right: 10px;
    }

    .author-name {
      font-size: $font_size_small;
    }
  }

  .book-info {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    text-align: left;
    padding: 15px;

    .book-title {

      h2 {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: $font_size_regular;
        font-weight: bold;
        text-transform: uppercase;
        margin-top: 5px;
      }

      .usd-icon {
        font-size: $font_size_xSmall;
        padding-left: 0.6rem;
        padding-right: 0.6rem;
        width: 26px;
        height: 26px;
        display: inline-block;

        .icon {
          margin-top: -1px;
        }
      }
    }
  }
}
</style>
